import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Typography, Input, Button, Space, DatePicker, Divider } from 'antd';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import {
  apiCreateCustomer,
  apiGetCustomerById,
  apiUpdateCustomer
} from '@services/ManageCustomers/CustomerServices';
import { showSuccess } from '@components/notification';
import moment from 'moment';
import dayjs from 'dayjs';
import { SelectBranch } from '@components/Select/SelectBranch';
import { SelectManageGroup } from '@components/Select/SelectManageGroup';
import { SelectParentDevice } from '@components/Select/SelectParentDevice';
import { SelectRoute } from '@components/Select/SelectRoute';

const { Title } = Typography;

const validationSchema = Yup.object({
  branch_code: Yup.string().required('Trường này là bắt buộc'),
  manage_group_code: Yup.string().required('Trường này là bắt buộc'),
  parent_device_code: Yup.string().required('Trường này là bắt buộc'),
  route: Yup.string().required('Trường này là bắt buộc'),
  code: Yup.string().required('Trường này là bắt buộc'),
  name: Yup.string().required('Trường này là bắt buộc'),
  address: Yup.string().required('Trường này là bắt buộc'),
  serial_number: Yup.string().required('Trường này là bắt buộc'),
  phone: Yup.string().required('Trường này là bắt buộc'),
  device_name: Yup.string().required('Trường này là bắt buộc'),
  install_time: Yup.mixed().required('Trường này là bắt buộc'),
  size: Yup.string().required('Trường này là bắt buộc'),
  type: Yup.string().required('Trường này là bắt buộc'),
  style: Yup.string().required('Trường này là bắt buộc'),
  inspection_date: Yup.mixed().required('Trường này là bắt buộc'),
  year_valid: Yup.string().required('Trường này là bắt buộc')
});

export const Add = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { id } = useParams();
  const [customer, setCustomer] = useState({});
  const [loadingFetch, setLoadingFetch] = useState(false);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const formattedValues = {
        ...values,
        install_time: values?.install_time
          ? dayjs(values?.install_time).format('DD/MM/YYYY')
          : null,
        inspection_date: values?.inspection_date
          ? dayjs(values?.inspection_date).format('DD/MM/YYYY')
          : null
      };
      id ? await apiUpdateCustomer(formattedValues, id) : await apiCreateCustomer(formattedValues);
      showSuccess({ message: id ? 'Cập nhật thành công' : 'Thêm mới thành công' });
      navigate('/customers');
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const fetch = async () => {
    try {
      setLoadingFetch(true);
      const res = await apiGetCustomerById(id);
      if (res) {
        setCustomer(res?.data?.data);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoadingFetch(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetch();
    }
  }, [id]);

  return (
    <>
      <Card
        title={'Thông tin khách hàng'}
        loading={loadingFetch}
        headStyle={{ backgroundColor: '#0051A9', color: '#FFF' }}
        bodyStyle={{ padding: '24px', paddingTop: '0px' }}>
        <Row gutter={[16, 16]}>
          <Col md={20} xs={24}>
            <Formik
              enableReinitialize
              initialValues={{
                branch: customer?.branch ?? '',
                branch_code: customer?.branch_code ?? '',
                manage_group: customer?.manage_group ?? '',
                manage_group_code: customer?.manage_group_code ?? '',
                parent_device: customer?.parent_device ?? '',
                parent_device_code: customer?.parent_device_code ?? '',
                route: customer?.route ?? '',
                code: customer?.code ?? '',
                name: customer?.name ?? '',
                address: customer?.address ?? '',
                serial_number: customer?.serial_number ?? '',
                phone: customer?.phone ?? '',
                device_name: customer?.device_name ?? '',
                install_time: customer?.install_time
                  ? dayjs(moment(customer?.install_time, 'DD/MM/YYYY').format('YYYY-MM-DD'))
                  : '',
                size: customer?.size ?? '',
                type: customer?.type ?? '',
                style: customer?.style ?? '',
                inspection_date: customer?.inspection_date
                  ? dayjs(moment(customer?.inspection_date, 'DD/MM/YYYY').format('YYYY-MM-DD'))
                  : '',
                year_valid: customer?.year_valid ?? '',
                longitude: customer?.longitude ?? '',
                latitude: customer?.latitude ?? '',
                note: customer?.note ?? ''
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}>
              {({ setFieldValue, errors, values }) => (
                <Form>
                  {console.log('Form errors:', errors)}
                  <Row gutter={[16, 16]}>
                    <Col md={24} xs={24}>
                      <Title level={5}>Tên KH(*)</Title>
                      <Field name="name">
                        {({ field }) => <Input {...field} placeholder="Nhập tên" />}
                      </Field>
                      <ErrorMessage name="name" component="div" style={{ color: 'red' }} />
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col md={8} xs={24}>
                      <Title level={5}>Nhà máy (*)</Title>
                      <Field name="branch_code">
                        {({ field }) => (
                          <SelectBranch
                            {...field}
                            value={field.value}
                            onChange={(value, option) => {
                              setFieldValue(field.name, value);
                              setFieldValue('branch', option.name);
                            }}
                          />
                        )}
                      </Field>
                      <ErrorMessage name="branch_code" component="div" style={{ color: 'red' }} />
                    </Col>
                    <Col md={8} xs={24}>
                      <Title level={5}>Tổ quản lý (*)</Title>
                      <Field name="manage_group_code">
                        {({ field }) => (
                          <SelectManageGroup
                            {...field}
                            value={field.value}
                            parentBranch={values.branch_code}
                            onChange={(value, option) => {
                              setFieldValue(field.name, value);
                              setFieldValue('manage_group', option.name);
                            }}
                          />
                        )}
                      </Field>
                      <ErrorMessage
                        name="manage_group_code"
                        component="div"
                        style={{ color: 'red' }}
                      />
                    </Col>
                    <Col md={8} xs={24}>
                      <Title level={5}>Đồng hồ tổng (*)</Title>
                      <Field name="parent_device_code">
                        {({ field }) => (
                          <SelectParentDevice
                            {...field}
                            value={field.value}
                            parentManageGroup={values.manage_group_code}
                            parentBranch={values.branch_code}
                            onChange={(value, option) => {
                              setFieldValue(field.name, value);
                              setFieldValue('parent_device', option.name);
                            }}
                          />
                        )}
                      </Field>
                      <ErrorMessage
                        name="parent_device_code"
                        component="div"
                        style={{ color: 'red' }}
                      />
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col md={8} xs={24}>
                      <Title level={5}>Mạng lưới (*)</Title>
                      <Field name="route">
                        {({ field }) => (
                          <SelectRoute
                            {...field}
                            value={field.value}
                            onChange={(value) => setFieldValue(field.name, value)}
                          />
                        )}
                      </Field>
                      <ErrorMessage name="route" component="div" style={{ color: 'red' }} />
                    </Col>
                    <Col md={8} xs={24}>
                      <Title level={5}>Danh bạ điểm dùng(*)</Title>
                      <Field name="code">
                        {({ field }) => <Input {...field} placeholder="Nhập danh bạ điểm dùng" />}
                      </Field>
                      <ErrorMessage name="code" component="div" style={{ color: 'red' }} />
                    </Col>
                    <Col md={8} xs={24}>
                      <Title level={5}>Số serial (*)</Title>
                      <Field name="serial_number">
                        {({ field }) => <Input {...field} placeholder="Nhập số serial" />}
                      </Field>
                      <ErrorMessage name="serial_number" component="div" style={{ color: 'red' }} />
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col md={24} xs={24}>
                      <Title level={5}>Địa chỉ (*)</Title>
                      <Field name="address">
                        {({ field }) => <Input {...field} placeholder="Nhập địa chỉ" />}
                      </Field>
                      <ErrorMessage name="address" component="div" style={{ color: 'red' }} />
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col md={8} xs={24}>
                      <Title level={5}>Số ĐT (*)</Title>
                      <Field name="phone">
                        {({ field }) => <Input {...field} placeholder="Nhập số điện thoại" />}
                      </Field>
                      <ErrorMessage name="phone" component="div" style={{ color: 'red' }} />
                    </Col>
                    <Col md={8} xs={24}>
                      <Title level={5}>Tên ĐH (*)</Title>
                      <Field name="device_name">
                        {({ field }) => <Input {...field} placeholder="Nhập tên đồng hồ" />}
                      </Field>
                      <ErrorMessage name="device_name" component="div" style={{ color: 'red' }} />
                    </Col>
                    <Col md={8} xs={24}>
                      <Title level={5}>Ngày lắp (*)</Title>
                      <DatePicker
                        value={values?.install_time}
                        style={{ width: '100%' }}
                        onChange={(date) => {
                          setFieldValue('install_time', date);
                        }}
                        placeholder="Chọn ngày lắp"
                        format="DD/MM/YYYY"
                      />
                      <ErrorMessage name="install_time" component="div" style={{ color: 'red' }} />
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col md={8} xs={24}>
                      <Title level={5}>Cỡ (*)</Title>
                      <Field name="size">
                        {({ field }) => <Input {...field} placeholder="Nhập cỡ" />}
                      </Field>
                      <ErrorMessage name="size" component="div" style={{ color: 'red' }} />
                    </Col>
                    <Col md={8} xs={24}>
                      <Title level={5}>Loại ĐH (*)</Title>
                      <Field name="type">
                        {({ field }) => <Input {...field} placeholder="Nhập loại ĐH" />}
                      </Field>
                      <ErrorMessage name="type" component="div" style={{ color: 'red' }} />
                    </Col>
                    <Col md={8} xs={24}>
                      <Title level={5}>Kiểu ĐH (*)</Title>
                      <Field name="style">
                        {({ field }) => <Input {...field} placeholder="Nhập kiểu ĐH" />}
                      </Field>
                      <ErrorMessage name="style" component="div" style={{ color: 'red' }} />
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col md={8} xs={24}>
                      <Title level={5}>Ngày kiểm định (*)</Title>
                      <DatePicker
                        value={values?.inspection_date}
                        style={{ width: '100%' }}
                        onChange={(date) => {
                          setFieldValue('inspection_date', date);
                        }}
                        placeholder="Chọn ngày lắp"
                        format="DD/MM/YYYY"
                      />
                      <ErrorMessage
                        name="inspection_date"
                        component="div"
                        style={{ color: 'red' }}
                      />
                    </Col>
                    <Col md={8} xs={24}>
                      <Title level={5}>Số năm hiệu lực (*)</Title>
                      <Field name="year_valid">
                        {({ field }) => <Input {...field} placeholder="Nhập năm hiệu lực" />}
                      </Field>
                      <ErrorMessage name="year_valid" component="div" style={{ color: 'red' }} />
                    </Col>
                    <Col md={8} xs={24}>
                      <Title level={5}>Vĩ độ (Latitude)</Title>
                      <Field name="latitude">
                        {({ field }) => <Input {...field} placeholder="Nhập vĩ độ" />}
                      </Field>
                    </Col>
                    <Col md={8} xs={24}>
                      <Title level={5}>Kinh độ (Longitude)</Title>
                      <Field name="longitude">
                        {({ field }) => <Input {...field} placeholder="Nhập kinh độ" />}
                      </Field>
                    </Col>
                    <Col md={16} xs={24}>
                      <Title level={5}>Ghi chú</Title>
                      <Field name="note">
                        {({ field }) => <Input {...field} placeholder="Nhập ghi chú" />}
                      </Field>
                    </Col>
                  </Row>
                  <Divider style={{ marginTop: '32px' }} />
                  <Row gutter={[16, 16]}>
                    <Col>
                      <Space size={16}>
                        <Button
                          type="primary"
                          onClick={() => navigate(-1)}
                          style={{
                            color: '#fff',
                            backgroundColor: '#ff9800',
                            height: '36px'
                          }}>
                          Quay lại
                        </Button>
                        <Button
                          loading={loading}
                          type="primary"
                          htmlType="submit"
                          style={{ backgroundColor: '#0051A9', height: '36px' }}>
                          {id ? 'Cập nhật' : 'Thêm mới'}
                        </Button>
                      </Space>
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          </Col>
        </Row>
      </Card>
    </>
  );
};
